import {differenceInYears, format, isValid, parse, parseISO} from 'date-fns';
import {toZonedTime} from 'date-fns-tz';

export const getTodayAt0Hours = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/**
 * Get the current date in Australia/Sydney timezone
 * Used for age calculations to ensure compliance with Australian legal requirements
 * @returns Date object representing "now" in Sydney timezone
 */
const getTodayInSydney = (): Date => {
  return toZonedTime(new Date(), 'Australia/Sydney');
};

export const toLocalISOString = (date: Date) => {
  let month = '' + (date.getMonth() + 1),
    day = '' + date.getDate();
  const year = date.getFullYear();

  if (month.length < 2) {
    month = '0' + month;
  }
  if (day.length < 2) {
    day = '0' + day;
  }

  return [year, month, day].join('-');
};

/**
 * @deprecated Use string-based DOB utilities instead
 * Format a date or date string to YYYY-MM-DD format for Zen API
 * @param date - Date object or string to format
 * @returns YYYY-MM-DD formatted string
 */
export const formatDateForZen = (date: Date | string): string => {
  if (typeof date === 'string') {
    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    // Otherwise parse and format
    const parsed = parseISO(date);
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : date;
  }
  // Convert Date object to YYYY-MM-DD using date-fns
  if (isValid(date)) {
    return format(date, 'yyyy-MM-dd');
  }
  // Fallback for invalid dates
  return '';
};

// ============================================================================
// Date of Birth Utilities (using date-fns)
// ============================================================================

/**
 * Parse day, month, year into YYYY-MM-DD string format
 * @param yyyy - Year as string (e.g., "1990")
 * @param mm - Month as string, 1-indexed with leading zero (e.g., "03" for March)
 * @param dd - Day as string with leading zero (e.g., "15")
 * @returns YYYY-MM-DD formatted string
 */
export const parseDateOfBirth = (yyyy: string, mm: string, dd: string): string => {
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Validate a date of birth string in YYYY-MM-DD format
 * @param dobString - Date string in YYYY-MM-DD format
 * @returns true if valid date, false otherwise (handles leap years, invalid dates like Feb 30, etc.)
 */
export const isValidDateOfBirth = (dobString: string): boolean => {
  if (!dobString || !/^\d{4}-\d{2}-\d{2}$/.test(dobString)) {
    return false;
  }

  const parsed = parse(dobString, 'yyyy-MM-dd', new Date());
  return isValid(parsed);
};

/**
 * Calculate age from a date of birth string
 * Uses Australia/Sydney timezone for "today" to ensure accurate age verification
 * @param dobString - Date string in YYYY-MM-DD format
 * @returns Age in years, or 0 if invalid date
 */
export const getAgeFromDateOfBirth = (dobString: string): number => {
  if (!isValidDateOfBirth(dobString)) {
    return 0;
  }

  const birthDate = parse(dobString, 'yyyy-MM-dd', new Date());
  const todayInSydney = getTodayInSydney();
  return differenceInYears(todayInSydney, birthDate);
};

/**
 * Check if person is at least 18 years old
 * @param dobString - Date string in YYYY-MM-DD format
 * @returns true if person is 18 or older, false otherwise
 */
export const isAtLeast18YearsOld = (dobString: string): boolean => {
  return getAgeFromDateOfBirth(dobString) >= 18;
};

/**
 * Format date of birth for display (e.g., "15 Mar 1990")
 * @param dobString - Date string in YYYY-MM-DD format
 * @returns Formatted date string, or empty string if invalid
 */
export const formatDateOfBirthForDisplay = (dobString: string): string => {
  if (!isValidDateOfBirth(dobString)) {
    return '';
  }

  const date = parse(dobString, 'yyyy-MM-dd', new Date());
  return format(date, 'dd MMM yyyy');
};

/**
 * Split a YYYY-MM-DD date string into day, month, year components
 * @param dobString - Date string in YYYY-MM-DD format
 * @returns Object with day, month, year as strings, or null values if invalid
 */
export const splitDateOfBirth = (
  dobString: string,
): {day: string; month: string; year: string} | {day: null; month: null; year: null} => {
  if (!dobString || !/^\d{4}-\d{2}-\d{2}$/.test(dobString)) {
    return {day: null, month: null, year: null};
  }

  const [year, month, day] = dobString.split('-');
  return {day, month, year};
};

/**
 * Convert ISO datetime string to YYYY-MM-DD date string
 * Extracts the calendar date portion without timezone conversion
 * (for DOB, we care about the date written on the birth certificate, not the moment in time)
 * @param isoString - ISO 8601 datetime string (e.g., "1990-03-15T13:00:00.000Z" or "1990-03-15T00:00:00+10:00")
 * @returns YYYY-MM-DD formatted string
 */
export const normalizeISOStringToDateOfBirth = (isoString: string): string => {
  if (!isoString) {
    return '';
  }

  // If already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoString)) {
    return isoString;
  }

  // Extract the date portion before 'T' to preserve the calendar date
  // This avoids timezone conversion issues (we want the date on the birth certificate, not UTC time)
  const datePortion = isoString.split('T')[0];

  // Validate that it's a proper date
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePortion)) {
    const parsed = parse(datePortion, 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) {
      return datePortion;
    }
  }

  return '';
};

/**
 * Format a member since date for display (e.g., "5 Jun 2021")
 * Uses date-fns `d MMM yyyy` format — no leading zero on day.
 * @param dateString - ISO 8601 date or datetime string (e.g., "2021-06-05" or "2021-06-05T00:00:00Z")
 * @returns Formatted date string, or empty string if absent or invalid
 */
export const formatMemberSinceDate = (dateString: string): string => {
  if (!dateString) return '';
  const parsed = parseISO(dateString);
  return isValid(parsed) ? format(parsed, 'd MMM yyyy') : '';
};
